import {
  Box,
  Divider,
  FormControl,
  IconButton,
  List,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { DoneTwoTone, ErrorTwoTone, ExitToApp, WarningTwoTone } from '@material-ui/icons'
import { ClosableDialog } from 'components/controls/dialogs'
import { DayPassPresenter } from 'components/domain/account'
import { CardmarketSidebarPresenter } from 'components/domain/cardmarket/CardmarketSidebarPresenter'
import { BugReportDialog } from 'components/domain/feedback/BugReportDialog'
import { ContactOptions } from 'components/domain/feedback/ContactOptions'
import { useUser } from 'components/providers/UserProvider'
import BSSLogo from 'img/bssLogo.png'
import cyberpunkLogo from 'img/cyberpunkLogo.webp'
import DBSLogo from 'img/DBSLogo.png'
import digimonLogo from 'img/digimonLogo.png'
import FABLogo from 'img/FABLogo.png'
import FFLogo from 'img/FFLogo.png'
import gundamLogo from 'img/gundam-logo.webp'
import lorcanaLogo from 'img/lorcanaLogo.png'
import magicLogo from 'img/magicLogo.png'
import onePieceLogo from 'img/onePieceLogoTransparent.png'
import pokemonLogo from 'img/pokemonLogo.png'
import powertoolsLogo from 'img/powertoolsLogo.png'
import riftboundLogo from 'img/riftboundLogo.png'
import sorceryLogo from 'img/sorceryLogo.webp'
import SWULogo from 'img/swuLogo.png'
import vanguardLogo from 'img/vanguardLogo.png'
import WSLogo from 'img/WSLogo.png'
import yugiohLogo from 'img/yugiohLogo.png'
import { getCardmarketStatus, recordUsage } from 'lib/api'
import { logoutUser } from 'lib/auth'
import { useAppContext } from 'lib/hooks'
import { useMobileSidebarStore } from 'lib/hooks/useMobileSidebarStore'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { currentCardmarketAccount } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Activity, CardmarketStatus, gameMetaData, IdGame, SubscriptionId } from 'shared'
import { CustomTooltip } from '../CustomTooltip'
import { MenuLink } from './MenuLink'
import './sidebar.css'

const logos: Record<IdGame, string> = {
  [IdGame.Magic]: magicLogo,
  [IdGame.Yugioh]: yugiohLogo,
  [IdGame.Pokemon]: pokemonLogo,
  [IdGame.Vanguard]: vanguardLogo,
  [IdGame.DBS]: DBSLogo,
  [IdGame.FF]: FFLogo,
  [IdGame.WS]: WSLogo,
  [IdGame.FAB]: FABLogo,
  [IdGame.Digimon]: digimonLogo,
  [IdGame.OnePiece]: onePieceLogo,
  [IdGame.Lorcana]: lorcanaLogo,
  [IdGame.BSS]: BSSLogo,
  [IdGame.SWU]: SWULogo,
  [IdGame.Riftbound]: riftboundLogo,
  [IdGame.Cyberpunk]: cyberpunkLogo,
  [IdGame.Gundam]: gundamLogo,
  [IdGame.Sorcery]: sorceryLogo,
}

const useStyles = makeStyles(() => {
  return {
    gameSelect: {
      '&:before': {
        border: 'none',
        content: 'none',
      },
      '&:after': {
        border: 'none',
        content: 'none ',
      },
      '& svg': {
        display: 'none',
      },
      '& .MuiSelect-select.MuiSelect-select': {
        padding: '0px',
      },
      padding: '2px',
      color: 'white',
      '& .MuiSelect-icon': {
        color: 'white',
      },
    },
  }
})

const cardmarketStatusColors = {
  [CardmarketStatus.normal]: '#3bb957',
  [CardmarketStatus.slow]: '#b9933b',
  [CardmarketStatus.down]: '#ca2424',
}

const cardmarketStatusSnackbar = {
  [CardmarketStatus.normal]: 'success',
  [CardmarketStatus.slow]: 'warning',
  [CardmarketStatus.down]: 'error',
} as const

const CardmarketStatusIcons = {
  [CardmarketStatus.normal]: DoneTwoTone,
  [CardmarketStatus.slow]: ErrorTwoTone,
  [CardmarketStatus.down]: WarningTwoTone,
} as const

interface RouteConfig {
  text: string
  path: string
  iconClass: string
  activeIconClass: string
  disabled: boolean
  hidden?: boolean
}

interface SharedSidebarProps {
  routes: RouteConfig[]
  disableSidebar: boolean
  // Same gate as RouteConfig.disabled: for the page links that are rendered
  // outside the `routes` loop, so they lock together with the others.
  disablePageLinks: boolean
  showTransactionWarningIcon: boolean
  setShowTransactionWarningIcon: (v: boolean) => void
  setShowSidebarWarning: (v: boolean) => void
  setOpenContactsDialog: (v: boolean) => void
  handleLogout: () => void
}

interface DesktopSidebarProps extends SharedSidebarProps {
  cardmarketStatus: CardmarketStatus
}

const DesktopSidebar = ({
  routes,
  disableSidebar,
  disablePageLinks,
  showTransactionWarningIcon,
  setShowTransactionWarningIcon,
  setShowSidebarWarning,
  setOpenContactsDialog,
  handleLogout,
  cardmarketStatus,
}: DesktopSidebarProps): JSX.Element => {
  const { user, hasPermission, activeGame, setActiveGame, fetchUser } = useUser()
  const [gameSelectOpen, setGameSelectOpen] = useState(false)
  const history = useHistory()
  const classes = useStyles()
  const { t } = useTranslation()

  const CardmarketStatusIcon = CardmarketStatusIcons[cardmarketStatus]

  return (
    <div className="main-sidebar">
      <div>
        <div className="title-box">
          <Box padding={1} data-testid="powertools-logo">
            <Link key="/" to="/">
              <img width="100%" src={powertoolsLogo} />
            </Link>
          </Box>
          <div>v2</div>
          <div className="logo-divider"></div>
          <FormControl>
            <Select
              className={classes.gameSelect}
              open={gameSelectOpen}
              onClose={() => setGameSelectOpen(false)}
              value={activeGame.idGame}
              disabled={disableSidebar}
              renderValue={() => (
                <>
                  <img width="100%" src={logos[activeGame.idGame]} />
                </>
              )}
              onOpen={() => setGameSelectOpen(true)}
              onChange={(e) => {
                setActiveGame(e.target.value as string)
              }}
              data-testid="select-game"
            >
              {gameMetaData.map((game) => (
                <MenuItem key={game.idGame} value={game.idGame} data-testid={`${game.name}-game`}>
                  <img width="100px" src={logos[game.idGame]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="logo-divider"></div>
          {
            // FIXME - this should be moved in a smarted check/function
            user?.currency !== 'CAD' && (
              <>
                <div>
                  <CardmarketSidebarPresenter />
                </div>
                <div className="logo-divider"></div>
              </>
            )
          }
        </div>
        <Divider />

        {routes.map((route) => {
          if (!route.hidden) {
            return (
              <MenuLink
                icon={route.iconClass}
                key={route.path}
                {...route}
                activeIcon={route.activeIconClass}
              />
            )
          }
        })}
      </div>

      <div className="bottom-menu">
        <List>
          <Divider />
          {false /* daypass currently disabled */ &&
            [SubscriptionId.Free, SubscriptionId.FreeRoca].includes(user!.subscription.id) && (
              <DayPassPresenter
                hasPaymentMethod={user!.outseta?.hasCreditCard || !!user!.stripePaymentMethod}
                cardmarketUsername={user!.cardmarketUsername!}
                dayPasses={user!.dayPasses}
                isProfi={currentCardmarketAccount(user!)?.professional === true}
                userId={user!._id}
                fetchUser={fetchUser}
                onSuccessfulPurchase={async () => {
                  await recordUsage(
                    Activity.PurchaseDaypass,
                    { profi: currentCardmarketAccount(user!)?.professional === true },
                    {
                      action: 'day_pass_purchase',
                      value: currentCardmarketAccount(user!)?.professional === true ? 15.99 : 3.99,
                      category: 'conversion',
                    }
                  )
                }}
              />
            )}
          <div>
            <i
              className={`fa-solid fa-circle-exclamation sidebar-warning-icon ${
                showTransactionWarningIcon ? 'd-inline' : 'd-none'
              }`}
            ></i>
            {hasPermission('access_back_office') && (
              <MenuLink
                icon="fa-light fa-lock-keyhole-open"
                activeIcon="fa-solid fa-lock-keyhole-open"
                text="Admin"
                path="/backoffice"
                onClick={() => {
                  history.push('/backoffice')
                }}
              />
            )}
            <MenuLink
              icon="fa-light fa-arrows-repeat"
              activeIcon="fa-solid fa-arrows-repeat"
              text={t('navigation.inventoryTransactions')}
              path="/inventoryTransactions"
              onClick={() => {
                setShowTransactionWarningIcon(false)
                setShowSidebarWarning(false)
                history.push('/inventoryTransactions')
              }}
              disabled={disablePageLinks}
            />
          </div>

          <MenuLink
            icon="fa-light fa-message"
            activeIcon="fa-solid fa-message"
            text={t('navigation.contactUs')}
            onClick={() => setOpenContactsDialog(true)}
          />
          <MenuLink
            icon="fa-light fa-user"
            activeIcon="fa-solid fa-user"
            text={t('navigation.myAccount')}
            path="/account"
          />
          <Box display="flex" marginLeft={1.3} alignItems="center" justifyContent="center">
            <CustomTooltip
              placement="top"
              title={
                <Box padding={1}>
                  <Typography>
                    <span> {t(`cardmarketStatus.${CardmarketStatus[cardmarketStatus]}`)}</span>
                  </Typography>
                  <Typography variant="caption">{t('cardmarketStatus.info')}</Typography>
                </Box>
              }
            >
              <CardmarketStatusIcon style={{ color: cardmarketStatusColors[cardmarketStatus] }} />
            </CustomTooltip>

            <CustomTooltip title={t('navigation.logout') as string}>
              <IconButton onClick={handleLogout}>
                <ExitToApp style={{ color: 'white' }} />
              </IconButton>
            </CustomTooltip>
          </Box>
        </List>
      </div>
    </div>
  )
}

interface MobileSidebarProps extends SharedSidebarProps {
  openedFullScreenMenu: boolean
  setOpenedFullScreenMenu: (v: boolean) => void
  fullScreenSelectGame: boolean
  setFullScreenSelectGame: (v: boolean) => void
}

const MobileSidebar = ({
  routes,
  disablePageLinks,
  showTransactionWarningIcon,
  setShowTransactionWarningIcon,
  setShowSidebarWarning,
  setOpenContactsDialog,
  handleLogout,
  openedFullScreenMenu,
  setOpenedFullScreenMenu,
  fullScreenSelectGame,
  setFullScreenSelectGame,
}: MobileSidebarProps): JSX.Element => {
  const { hasPermission, activeGame, setActiveGame } = useUser()
  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation()
  const mobileSidebar = useMobileSidebarStore()
  const isOnInventoryPage =
    location.pathname === '/stock-pricing' || location.pathname === '/listing-and-appraisal'
  const isOnAccountPage = location.pathname === '/account'

  const openFullScreenMenu = () => {
    setOpenedFullScreenMenu(!openedFullScreenMenu)
  }

  return (
    <div>
      <div className="mobile-sidebar ps-4 d-flex justify-content-start align-items-center">
        <i
          data-testid="open-mobile-menu"
          onClick={openFullScreenMenu}
          className={`sidebar-menu fa-light ${
            openedFullScreenMenu ? 'fa-xmark opened' : 'fa-bars'
          }`}
        />
        {isOnInventoryPage && !openedFullScreenMenu && (
          <i
            data-testid="filter-dock-mobile"
            onClick={() => mobileSidebar.setFilterDock(true)}
            className="fa-light fa-sidebar sidebar-menu ms-3"
            style={{ cursor: 'pointer' }}
          />
        )}

        {isOnAccountPage && !openedFullScreenMenu && (
          <i
            data-testid="open-mobile-account-sidebar"
            onClick={() => mobileSidebar.setAccount(true)}
            className="fa-light fa-sidebar sidebar-menu ms-3"
            style={{ cursor: 'pointer' }}
          />
        )}
      </div>
      <div className={`full-screen-menu-overlay ${openedFullScreenMenu ? 'open' : 'closed'}`}>
        <div className="menu-content ">
          <div>
            <div className="d-flex flex-row align-items-center w-100">
              <Link to="/" onClick={openFullScreenMenu}>
                <img src={powertoolsLogo} className="powertools-logo" alt="Logo" />
              </Link>

              <div className="w-100 text-center ">
                <img
                  onClick={() => setFullScreenSelectGame(true)}
                  className="game-logo"
                  src={logos[activeGame.idGame]}
                />
              </div>
            </div>
          </div>
          <div className="logo-divider"></div>
          <List>
            {routes.map((route) =>
              !route.hidden ? (
                <MenuLink
                  icon={route.iconClass}
                  key={route.path}
                  {...route}
                  activeIcon={route.activeIconClass}
                />
              ) : null
            )}

            {hasPermission('access_back_office') && (
              <MenuLink
                icon="fa-light fa-lock-keyhole-open"
                activeIcon="fa-solid fa-lock-keyhole-open"
                text="Admin"
                path="/backoffice"
                onClick={() => {
                  history.push('/backoffice')
                }}
              />
            )}
            <div>
              <i
                className={`fa-solid fa-circle-exclamation sidebar-warning-icon ${
                  showTransactionWarningIcon ? 'd-inline' : 'd-none'
                }`}
              ></i>
              <MenuLink
                icon="fa-light fa-arrows-repeat"
                activeIcon="fa-solid fa-arrows-repeat"
                text={t('navigation.inventoryTransactions')}
                path="/inventoryTransactions"
                onClick={() => {
                  setShowTransactionWarningIcon(false)
                  setShowSidebarWarning(false)
                  history.push('/inventoryTransactions')
                }}
                disabled={disablePageLinks}
              />
            </div>
            <MenuLink
              icon="fa-light fa-message"
              activeIcon="fa-solid fa-message"
              text={t('navigation.contactUs')}
              onClick={() => {
                setOpenedFullScreenMenu(false)
                setTimeout(() => setOpenContactsDialog(true), 300)
              }}
            />
            <MenuLink
              icon="fa-light fa-user"
              activeIcon="fa-solid fa-user"
              text={t('navigation.myAccount')}
              path="/account"
            />
            <MenuLink
              icon="fa-light fa-arrow-right-from-bracket"
              activeIcon="fa-solid fa-arrow-right-from-bracket"
              text={t('navigation.logout')}
              onClick={() => {
                handleLogout()
              }}
            />
          </List>
        </div>
      </div>

      <div className={`select-game pt-3 ${fullScreenSelectGame ? 'visible' : 'hidden'}`}>
        {gameMetaData.slice(1).map((game) => (
          <div
            className="select-game-item"
            onClick={() => {
              if (activeGame !== game) setActiveGame(game.idGame.toString())
              else setFullScreenSelectGame(false)
            }}
            key={game.idGame}
            data-testid={`${game.name}-game`}
          >
            <img width="100px" src={logos[game.idGame]} />
          </div>
        ))}
      </div>
    </div>
  )
}

export const Sidebar = ({
  setBannerMessage,
}: {
  setBannerMessage: (banner: { _id: string; message: string } | undefined) => void
}): JSX.Element => {
  const { setUser, user } = useUser()
  const { setAuthenticated } = useAppContext()
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()
  const { t } = useTranslation()
  const { isMobile } = useAppContext()
  const [openedFullScreenMenu, setOpenedFullScreenMenu] = useState(false)
  const [fullScreenSelectGame, setFullScreenSelectGame] = useState(false)
  const [openContactsDialog, setOpenContactsDialog] = useState(false)
  const [openBugReportDialog, setOpenBugReportDialog] = useState(false)

  const [cardmarketStatus, setCardmarketStatus] = useState<CardmarketStatus>(
    CardmarketStatus.normal
  )
  const [disableSidebar, setDisableSidebar] = useState(false)

  if (!user) return <></>

  useEffect(() => {
    // Disable the navigation (but NOT logout — it has no `disabled` guard) when the user has no PT
    // inventory, or when they are being force-held on the graded-stock sync (featureFlags.forceGradeSync).
    // The forceGradeSync condition mirrors AppLayout's forced-redirect guard, so the sidebar is disabled
    // exactly on the page the user is pinned to until they complete the sync.
    if (!user?.ptInventory || (user?.featureFlags?.forceGradeSync && user?.isTester?.ptInventory)) {
      setDisableSidebar(true)
    } else {
      setDisableSidebar(false)
    }
  }, [user])

  const { showSidebarWarning, setShowSidebarWarning } = useTransactionsSpinnerStore()
  const [showTransactionWarningIcon, setShowTransactionWarningIcon] = useState(false)

  useEffect(() => {
    if (showSidebarWarning) setShowTransactionWarningIcon(true)
  }, [showSidebarWarning])

  useEffect(() => {
    const fetchStatus = async (): Promise<void> => {
      try {
        const response = await getCardmarketStatus()

        setBannerMessage(response?.banner)

        if (cardmarketStatus !== response.status) {
          setCardmarketStatus(response.status)
          if ([cardmarketStatus, response.status].includes(CardmarketStatus.down))
            enqueueSnackbar(t(`cardmarketStatus.changedTo.${CardmarketStatus[response.status]}`), {
              variant: cardmarketStatusSnackbar[response.status],
            })
        }
      } catch (err) {
        if (cardmarketStatus !== CardmarketStatus.down) {
          setCardmarketStatus(CardmarketStatus.down)
          enqueueSnackbar(t(`cardmarketStatus.changedTo.down`), {
            variant: 'error',
          })
        }
      }
    }
    fetchStatus()
    const statusInterval = setInterval(fetchStatus, 30 * 1000)

    return () => clearInterval(statusInterval)
  }, [cardmarketStatus])

  const handleLogout = (): void => {
    logoutUser()
    setAuthenticated(false)
    //@ts-ignore
    setUser(null)
    history.push('/signin')
  }

  useEffect(() => {
    function updateWindowDimensions() {
      if (window.innerWidth > 1300) setOpenedFullScreenMenu(false)
    }

    window.addEventListener('resize', updateWindowDimensions)
    return () => window.removeEventListener('resize', updateWindowDimensions)
  }, [])

  // used to prevent unwanted page scrolling when the menu is open
  useEffect(() => {
    const open = openedFullScreenMenu || fullScreenSelectGame
    if (!open) return

    const y = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${y}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    return () => {
      const top = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      window.scrollTo(0, top ? -parseInt(top, 10) : 0)
    }
  }, [openedFullScreenMenu, fullScreenSelectGame])

  const isDisabled = () => {
    return user.subscription.allFeaturesDisabled || user.featureFlags?.banned || disableSidebar
  }

  const routes: RouteConfig[] = [
    {
      text: t('navigation.listingAndAppraisal'),
      path: '/listing-and-appraisal',
      iconClass: 'fa-light fa-rectangle-history-circle-plus',
      activeIconClass: 'fa-solid fa-rectangle-history-circle-plus',
      disabled: isDisabled(),
    },
    {
      text: t('navigation.pricingSettings'),
      path: '/pricing-settings',
      iconClass: 'fa-light fa-chart-mixed-up-circle-dollar',
      activeIconClass: 'fa-solid fa-chart-mixed-up-circle-dollar',
      disabled: isDisabled(),
    },
    {
      text: t('navigation.stockPricing'),
      path: '/stock-pricing',
      iconClass: 'fa-light fa-garage',
      activeIconClass: 'fa-solid fa-garage',
      disabled: isDisabled(),
    },
    {
      text: t('navigation.orders'),
      path: '/orders',
      iconClass: 'fa-light fa-file-lines',
      activeIconClass: 'fa-solid fa-file-lines',
      disabled: isDisabled(),
      hidden: false,
    },
    {
      text: t('navigation.taxation'),
      path: '/taxation',
      iconClass: 'fa-sharp fa-light fa-coins',
      activeIconClass: 'fa-sharp fa-solid fa-coins',
      disabled: isDisabled(),
      hidden: !user.featureFlags?.taxation,
    },
    {
      text: t('navigation.exports'),
      path: '/exports',
      iconClass: 'fa-sharp fa-light fa-cloud-arrow-down',
      activeIconClass: 'fa-sharp fa-solid fa-cloud-arrow-down',
      disabled: isDisabled(),
    },
  ]

  const sharedProps: SharedSidebarProps = {
    routes,
    disableSidebar,
    disablePageLinks: isDisabled(),
    showTransactionWarningIcon,
    setShowTransactionWarningIcon,
    setShowSidebarWarning,
    setOpenContactsDialog,
    handleLogout,
  }

  return (
    <>
      {openContactsDialog && (
        <ClosableDialog
          open={openContactsDialog}
          onClose={() => setOpenContactsDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <h3>{t('navigation.contactUs')}</h3>
          <ContactOptions
            onSupportRequest={() => {
              setOpenContactsDialog(false)
              setOpenBugReportDialog(true)
            }}
          />
        </ClosableDialog>
      )}
      <BugReportDialog open={openBugReportDialog} onClose={() => setOpenBugReportDialog(false)} />

      {isMobile ? (
        <MobileSidebar
          {...sharedProps}
          openedFullScreenMenu={openedFullScreenMenu}
          setOpenedFullScreenMenu={setOpenedFullScreenMenu}
          fullScreenSelectGame={fullScreenSelectGame}
          setFullScreenSelectGame={setFullScreenSelectGame}
        />
      ) : (
        <DesktopSidebar {...sharedProps} cardmarketStatus={cardmarketStatus} />
      )}
    </>
  )
}
